import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/context/AuthContext';
import BottomNav from '@/components/BottomNav';
import { useTheme } from '@/context/ThemeContext';
import { useLanguage } from '@/i18n';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  User, 
  MapPin, 
  Palette, 
  LogOut,
  Moon,
  Sun,
  Monitor,
  Store,
  Languages,
  ChevronRight,
  Bell,
  BellOff,
  Shield,
  Download,
  Crown,
  Loader2,
  Clock,
  BarChart3
} from 'lucide-react';

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, updateUser, logout, api } = useAuth();
  const { theme, setTheme, autoTheme, setAutoTheme } = useTheme();
  const { t, language, setLanguage, languages } = useLanguage();
  const { 
    isSupported: pushSupported, 
    isSubscribed: pushSubscribed, 
    permission: pushPermission,
    loading: pushLoading,
    toggleSubscription 
  } = usePushNotifications();
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || '',
    commune: user?.commune || '',
    bio: user?.bio || ''
  });

  const handleExportData = async () => {
    setExportLoading(true);
    try {
      const response = await api.get('/user/export', {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bourbon_data_export_${user.user_id}_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Données exportées avec succès !');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    } finally {
      setExportLoading(false);
    }
  };

  const handlePushToggle = async () => {
    const success = await toggleSubscription();
    if (success) {
      toast.success(pushSubscribed ? t('settings.pushNotifications') + ' désactivées' : t('settings.pushNotifications') + ' activées');
    } else if (pushPermission === 'denied') {
      toast.error('Les notifications sont bloquées. Veuillez les autoriser dans les paramètres de votre navigateur.');
    }
  };

  const communes = [
    // Nord
    { id: 'Saint-Denis', name: 'Saint-Denis' },
    { id: 'Sainte-Marie', name: 'Sainte-Marie' },
    { id: 'Sainte-Suzanne', name: 'Sainte-Suzanne' },
    // Est
    { id: 'Saint-André', name: 'Saint-André' },
    { id: 'Bras-Panon', name: 'Bras-Panon' },
    { id: 'Saint-Benoît', name: 'Saint-Benoît' },
    { id: 'Sainte-Rose', name: 'Sainte-Rose' },
    { id: 'Plaine-des-Palmistes', name: 'La Plaine-des-Palmistes' },
    // Sud
    { id: 'Saint-Pierre', name: 'Saint-Pierre' },
    { id: 'Saint-Louis', name: 'Saint-Louis' },
    { id: 'Saint-Joseph', name: 'Saint-Joseph' },
    { id: 'Saint-Philippe', name: 'Saint-Philippe' },
    { id: 'Petite-Île', name: 'Petite-Île' },
    { id: 'Le-Tampon', name: 'Le Tampon' },
    { id: 'Entre-Deux', name: 'Entre-Deux' },
    { id: 'Cilaos', name: 'Cilaos' },
    { id: 'Étang-Salé', name: "L'Étang-Salé" },
    { id: 'Les-Avirons', name: 'Les Avirons' },
    // Ouest
    { id: 'Saint-Paul', name: 'Saint-Paul' },
    { id: 'Saint-Leu', name: 'Saint-Leu' },
    { id: 'Trois-Bassins', name: 'Trois-Bassins' },
    { id: 'La-Possession', name: 'La Possession' },
    { id: 'Le-Port', name: 'Le Port' },
    // Cirque
    { id: 'Salazie', name: 'Salazie' }
  ];

  const themes = [
    { id: 'light', name: t('settings.themes.light'), icon: Sun },
    { id: 'dark', name: t('settings.themes.dark'), icon: Moon },
    { id: 'system', name: t('settings.themes.system'), icon: Monitor }
  ];

  const handleSave = async () => {
    setLoading(true);
    const result = await updateUser(form);
    setLoading(false);

    if (result.success) {
      toast.success(t('common.save') + ' ✓');
    } else {
      toast.error(result.error);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success(t('auth.logout') + ' ✓');
    navigate('/', { replace: true });
  };

  return (
    <div className="min-h-screen tropic-bg pb-28 md:pb-12 md:pt-16">
      {/* Header */}
      <header className="sticky top-0 md:top-16 z-40 glass border-b">
        <div className="container mx-auto max-w-2xl px-4 py-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-xl font-bold">{t('settings.title')}</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto max-w-2xl px-4 py-6 space-y-6">
        {/* Language Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Languages className="h-5 w-5" />
              {t('settings.language')}
            </CardTitle>
            <CardDescription>
              {language === 'cr' ? 'Shwazi out lang préféré' : 'Choisissez votre langue préférée'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {languages.map((lang) => (
                <Button
                  key={lang.code}
                  variant={language === lang.code ? 'default' : 'outline'}
                  className="flex items-center gap-2 h-auto py-3 rounded-xl"
                  onClick={() => setLanguage(lang.code)}
                  data-testid={`lang-${lang.code}`}
                >
                  <span className="text-xl">{lang.flag}</span>
                  <span>{lang.name}</span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Push Notifications */}
        {pushSupported && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {pushSubscribed ? <Bell className="h-5 w-5" /> : <BellOff className="h-5 w-5" />}
                {t('settings.pushNotifications')}
              </CardTitle>
              <CardDescription>
                {language === 'cr' 
                  ? 'Résevwar bann alèrt an tan réèl' 
                  : 'Recevez des alertes en temps réel'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium">
                    {pushSubscribed 
                      ? (language === 'cr' ? 'Notifikasyon aktivé' : 'Notifications activées')
                      : (language === 'cr' ? 'Notifikasyon désaktivé' : 'Notifications désactivées')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {pushPermission === 'denied' 
                      ? (language === 'cr' ? 'Bloké par navigatèr' : 'Bloquées par le navigateur')
                      : (language === 'cr' ? 'Alèrt, mésaz, abonman' : 'Alertes, messages, abonnements')}
                  </p>
                </div>
                <Switch
                  checked={pushSubscribed}
                  onCheckedChange={handlePushToggle}
                  disabled={pushLoading || pushPermission === 'denied'}
                  data-testid="push-notifications-toggle"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Profile Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              {t('profile.myProfile')}
            </CardTitle>
            <CardDescription>{t('profile.editProfile')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('auth.name')}</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="commune">{t('auth.commune')}</Label>
              <Select 
                value={form.commune}
                onValueChange={(value) => setForm({ ...form, commune: value })}
              >
                <SelectTrigger data-testid="commune-select">
                  <SelectValue placeholder={t('auth.selectCommune')} />
                </SelectTrigger>
                <SelectContent>
                  {communes.map((commune) => (
                    <SelectItem key={commune.id} value={commune.id}>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-3 w-3" />
                        {commune.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio">{t('profile.bio')}</Label>
              <Textarea
                id="bio"
                placeholder={t('profile.bioPlaceholder')}
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                rows={3}
                maxLength={200}
                data-testid="bio-input"
              />
              <p className="text-xs text-muted-foreground text-right">{form.bio?.length || 0}/200</p>
            </div>

            <Button 
              onClick={handleSave}
              disabled={loading}
              className="w-full rounded-xl"
              data-testid="save-profile-btn"
            >
              {loading ? <span className="spinner mr-2" /> : null}
              {t('common.save')}
            </Button>
          </CardContent>
        </Card>

        {/* Theme Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              {t('settings.theme')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              {themes.map((themeItem) => (
                <Button
                  key={themeItem.id}
                  variant={theme === themeItem.id && !autoTheme ? 'default' : 'outline'}
                  className="flex flex-col items-center gap-2 h-auto py-4 rounded-xl"
                  onClick={() => {
                    setAutoTheme(false);
                    setTheme(themeItem.id);
                  }}
                  data-testid={`theme-${themeItem.id}`}
                >
                  <themeItem.icon className="h-5 w-5" />
                  <span className="text-sm">{themeItem.name}</span>
                </Button>
              ))}
            </div>
            
            {/* Auto Theme Option */}
            <div className="flex items-center justify-between pt-2 border-t">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">
                    {language === 'cr' ? 'Mòd otomatik' : 'Mode automatique'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {language === 'cr' ? 'Sonb 20h-7h, Klèr sinon' : 'Sombre 20h-7h, Clair sinon'}
                  </p>
                </div>
              </div>
              <Switch
                checked={autoTheme}
                onCheckedChange={setAutoTheme}
                data-testid="auto-theme-toggle"
              />
            </div>
          </CardContent>
        </Card>

        {/* Premium Subscription */}
        <Card 
          className="cursor-pointer hover:bg-muted/50 transition-colors border-primary/20 bg-gradient-to-r from-primary/5 to-transparent"
          onClick={() => navigate('/subscription')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Crown className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium flex items-center gap-2">
                    {language === 'cr' ? 'Premium' : 'Premium'}
                    {user?.is_premium && (
                      <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">Actif</span>
                    )}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {user?.is_premium 
                      ? (language === 'cr' ? 'Jèr ou abonman' : 'Gérer votre abonnement')
                      : (language === 'cr' ? 'Déblok tou bann fonksyonalité' : 'Débloquer toutes les fonctionnalités')}
                  </p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        {/* Stats (Premium) */}
        <Card 
          className={`cursor-pointer hover:bg-muted/50 transition-colors ${!user?.is_premium ? 'opacity-60' : ''}`}
          onClick={() => navigate('/settings/stats')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <BarChart3 className="h-5 w-5 text-primary" />
                <div>
                  <p className="font-medium flex items-center gap-2">
                    {language === 'cr' ? 'Statistik' : 'Statistiques'}
                    {!user?.is_premium && (
                      <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">Premium</span>
                    )}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {language === 'cr' ? 'Analiz ou odyans' : 'Analysez votre audience'}
                  </p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        {/* Commerce Settings */}
        <Card 
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => navigate('/commerce-settings')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Store className="h-5 w-5 text-primary" />
                <div>
                  <p className="font-medium">{t('commerce.title')}</p>
                  <p className="text-sm text-muted-foreground">
                    {user?.is_commerce ? t('commerce.verified') : t('commerce.verify')}
                  </p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        {/* Admin Panel - Only for admins */}
        {user?.is_admin && (
          <Card 
            className="cursor-pointer hover:bg-muted/50 transition-colors border-primary/50"
            onClick={() => navigate('/admin')}
            data-testid="admin-panel-link"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Shield className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">{language === 'cr' ? 'Administrasyon' : 'Administration'}</p>
                    <p className="text-sm text-muted-foreground">
                      {language === 'cr' ? 'Jèr laplikasyon' : 'Gérer l\'application'}
                    </p>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Data Export - GDPR */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5" />
              {language === 'cr' ? 'Eksport dann' : 'Exporter mes données'}
            </CardTitle>
            <CardDescription>
              {language === 'cr' 
                ? 'Télésharz tou ou bann doné an JSON (RGPD)'
                : 'Téléchargez toutes vos données en JSON (RGPD)'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              variant="outline" 
              className="w-full rounded-xl"
              onClick={handleExportData}
              disabled={exportLoading}
              data-testid="export-data-btn"
            >
              {exportLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {language === 'cr' ? 'Préparasyon...' : 'Préparation...'}
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  {language === 'cr' ? 'Télésharz' : 'Télécharger'}
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Account */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <LogOut className="h-5 w-5" />
              {t('settings.account')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button 
              variant="destructive" 
              className="w-full rounded-xl"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              {t('settings.logout')}
            </Button>
          </CardContent>
        </Card>

        {/* App Info */}
        <div className="text-center text-sm text-muted-foreground pt-4">
          <p className="font-bold text-primary">Bourbon</p>
          <p>{t('settings.version')} 2.4.0</p>
          <p className="mt-2">{language === 'cr' ? 'Fé èk ❤️ La Rényon' : 'Fait avec ❤️ à La Réunion'}</p>
        </div>
      </main>
      <BottomNav />
    </div>
  );
};

export default SettingsPage;
